import {
  useOrganizationsServiceOrganizationsAccessList,
  useOrganizationsServiceOrganizationsTeamsPartialUpdate,
  useOrganizationsServiceOrganizationsTeamsRetrieveKey,
  useOrganizationsServiceOrganizationsTeamUsersList,
  useOrganizationsServiceOrganizationsTeamUsersListKey
} from '@organization/api/queries'
import {
  OrganizationUsers,
  TeamList,
  TeamUsersDetail
} from '@organization/api/requests'
import { TitleActionButton } from '@organization/components'
import { paths } from '@organization/router'
import {
  useInvalidateQueries,
  usePaginationConfig,
  useQueryUpdate,
  useSessionContext
} from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { Button, Flex, Space } from 'antd'
import { UserAdd } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { TeamForm } from '../Team'
import { UsersTable } from '../Users'

type Props = {
  team?: TeamList
}

export const UsersTableContainer = ({ team }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { t } = useTranslation()
  const { params, updateQuery } = useQueryUpdate()

  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState('')
  const { user } = useSessionContext()
  const { tab } = useParams()
  const navigate = useNavigate()
  const { invalidateQueries } = useInvalidateQueries()

  const queryParams = {
    organization: Number(user?.organization?.id),
    page: params?.page,
    pageSize: params?.pageSize,
    ...(search && { search }),
    ...(!!ordering && { ordering }),
    ...(!!team && { team: Number(team?.id) })
  }

  const { data, isLoading } = useOrganizationsServiceOrganizationsAccessList(
    queryParams,
    [queryParams],
    {
      enabled: (!!user?.organization?.id && tab === 'users') || !!team?.id
    }
  )

  const { data: teamUsers, isLoading: isTeamUsersLoading } =
    useOrganizationsServiceOrganizationsTeamUsersList(
      queryParams,
      [queryParams],
      {
        enabled: !!team?.id
      }
    )

  const { mutate: editTeam, isPending } =
    useOrganizationsServiceOrganizationsTeamsPartialUpdate({
      onSuccess: () => {
        invalidateQueries([
          useOrganizationsServiceOrganizationsTeamsRetrieveKey,
          Number(team?.id),
          useOrganizationsServiceOrganizationsTeamUsersListKey,
          Number(team?.id)
        ])

        setIsModalOpen(false)
      },
      onError: (e) => showApiErrors(e)
    })

  return (
    <Flex vertical gap={20}>
      <TitleActionButton
        title={`${t('users.my_people')} (${data?.count ?? 0})`}
        onSearch={(search) => setSearch(search)}
        renderActionButton={(isAllowRole) => (
          <Space>
            {isAllowRole && (
              <Button
                type="primary"
                icon={<UserAdd size={14} />}
                onClick={() => {
                  if (team) {
                    setIsModalOpen(true)
                  } else {
                    navigate(paths.users.invite)
                  }
                }}
              >
                {team ? t('users.add_user_to_team') : t('users.add_user')}
              </Button>
            )}
          </Space>
        )}
      />

      <UsersTable
        isOnTeamContainer={!!team}
        usersData={
          (team ? teamUsers?.results : data?.results) as (OrganizationUsers &
            TeamUsersDetail)[]
        }
        isLoading={isLoading || isTeamUsersLoading}
        pagination={{
          ...usePaginationConfig(team ? teamUsers : data),
          onChange: (page: number, pageSize: number) =>
            updateQuery({
              ...params,
              page: page.toString(),
              pageSize: pageSize.toString()
            })
        }}
        onChangeSortValue={(key) => setOrdering(key)}
      />

      {isModalOpen && (
        <TeamForm
          key="add-team-users"
          title={t('users.add_team_users')}
          okText={t('button.add')}
          team={team}
          hideTitle
          isLoading={isPending}
          onSubmit={(values) => {
            if (!team?.id) return
            editTeam({ id: team?.id, requestBody: values })
          }}
          onCancel={() => setIsModalOpen(false)}
        />
      )}
    </Flex>
  )
}
